import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import useSWR from 'swr';
import axios from 'axios';
import { AuthContext } from './use-auth';
import { URL_BASE } from '../const/general';

/**
 * SWRのリトライ処理（最大2回、404/500では停止）
 */
const handleRetry = (error, _key, _config, revalidate, { retryCount }) => {
  if (error._isRefreshFailed || [404, 500].includes(error.status)) return;
  if (retryCount >= 2) return;
  setTimeout(() => revalidate({ retryCount: retryCount + 1 }), 5000);
};

/**
 * SWR共通フック（fetcherと認証失敗時の遷移フラグ指定）
 */
const useCommonSWR = (url, fetcher, navigateOnFail = true) => {
  const navigate = useNavigate();
  const fullUrl = URL_BASE + url;

  const { data, error } = useSWR(
    [fullUrl],
    async ([url]) => {
      try {
        return await fetcher(url);
      } catch (err) {
        if ((err._is401 || err._isRefreshFailed) && navigateOnFail) {
          navigate('/signin');
          return;
        }
        throw err;
      }
    },
    {
      onErrorRetry: handleRetry,
      refreshInterval: 300000,
      dedupingInterval: 500,
      revalidateOnMount: true,
      revalidateIfStale: true,
    }
  );

  return {
    data,
    loading: data === undefined && !error,
    error
  };
};

// refreshTokenAndRetry: 新トークンを取得して直接使う
const refreshTokenAndRetry = async (url, prefCodeAry) => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();

    return await fetchWithToken(url, prefCodeAry, idToken);
  } catch (err) {
    err._isRefreshFailed = true;
    throw err;
  }
};

// トークンを引数で受け取る関数
const fetchWithToken = async (url, prefCodeAry, idToken) => {
  const urlObj = new URL(url, window.location.origin);
  const key = urlObj.pathname.split('/').pop();

  // pref_code 不要な key（202508時点ではなし）
  const skipPref = [''];

  // APIコール用のURLを取得 
  // 必要に応じてpref_codeをパラメータに追加
  if (!skipPref.includes(key) && !urlObj.searchParams.has('pref_code')) {
    prefCodeAry.forEach((code) => {
      urlObj.searchParams.append('pref_code', code);
    });
  }
  const queryString = urlObj.searchParams.toString();
  const finalUrl = queryString
    ? `${urlObj.pathname}?${queryString}`
    : `${urlObj.pathname}`;

  // API からpresignedURLを取得
  const presignedRes = await axios.get(finalUrl, {
    headers: { Authorization: `Bearer ${idToken}` }
  });

  // presignedURLでデータフェッチ
  const res = await axios.get(presignedRes.data.downloadUrls[0], {
    // headers は完全に空にするのが安全
    headers: {},
    withCredentials: false,
  });
  // presignedURLでフェッチしたデータを返す
  return res.data;
};

const fetchFromPresignedUrl = async (url, prefCodeAry) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    return await fetchWithToken(url, prefCodeAry, idToken);
  } catch (error) {
    if (error.response?.status === 401) {
      return await refreshTokenAndRetry(url, prefCodeAry);
    }
    throw error;
  }
};

export const useDataFromS3 = (url) => {
  // 県コードの取得
  const { enabledPrefs } = useContext(AuthContext);
  const prefCodeAry = enabledPrefs?.split(',') ?? [];

  // url が null またはprefCodeAryが取得できない場合、SWRに null を渡してフェッチを止める
  const shouldFetch = !!url && prefCodeAry.length > 0 && prefCodeAry[0] !== "";
  return useCommonSWR(
    shouldFetch ? url : null, // SWR的に null でフェッチ停止
    shouldFetch
      ? (urlToFetch) => fetchFromPresignedUrl(urlToFetch, prefCodeAry)
      : null // fetcherも無効化
  );
};